import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const useIsoLayoutEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

export function CoverflowCarousel({
  slides,
  rotate = 44,
  depth = 0.6,
  perspective = 3,
  falloff = 0.56,
  fade = 0.1,
  cardWidth = "clamp(220px, 28vw, 380px)",
  gap = 0.05,
  loop = true,
  showCaption = false,
  showPagination = false,
  showNavigation = true,
  label = "Cover carousel",
  className = "",
  cardClassName = "",
}) {
  const count = slides.length;

  const frameRef = React.useRef(null);
  const cardRefs = React.useRef([]);
  const posRef = React.useRef(0);
  const targetRef = React.useRef(0);
  const widthRef = React.useRef(0);
  const rafRef = React.useRef(null);
  const dragRef = React.useRef(null);

  const [selected, setSelected] = React.useState(0);

  const indexAt = React.useCallback(
    (pos) => ((Math.round(pos) % count) + count) % count,
    [count],
  );

  const paint = React.useCallback(() => {
    const width = widthRef.current;
    if (!width) return;
    const pitch = width * (1 + gap);
    const pos = posRef.current;

    cardRefs.current.forEach((card, index) => {
      if (!card) return;

      let offset = index - pos;
      if (loop) {
        offset = ((offset % count) + count) % count;
        if (offset > count / 2) offset -= count;
      }

      const distance = Math.abs(offset);
      const ramp = Math.pow(distance, falloff);
      const tilt = Math.min(rotate * ramp, 82) * Math.sign(offset);

      card.style.transform =
        `translateX(calc(-50% + ${offset * pitch}px)) ` +
        `translateZ(${-depth * width * ramp}px) rotateY(${-tilt}deg)`;

      const edge = loop ? Math.min(1, Math.max(0, count / 2 - distance)) : 1;
      card.style.opacity = String(Math.max(0, 1 - fade * distance) * edge);
      card.style.zIndex = String(100 - Math.round(distance));
    });
  }, [count, depth, fade, falloff, gap, loop, rotate]);

  const settle = React.useCallback(
    (target) => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      targetRef.current = target;
      setSelected(indexAt(target));

      const step = () => {
        const remaining = target - posRef.current;
        if (Math.abs(remaining) < 0.0004) {
          posRef.current = target;
          paint();
          rafRef.current = null;
          return;
        }
        posRef.current += remaining * 0.16;
        paint();
        rafRef.current = requestAnimationFrame(step);
      };
      rafRef.current = requestAnimationFrame(step);
    },
    [indexAt, paint],
  );

  const clamp = React.useCallback(
    (pos) => (loop ? pos : Math.max(0, Math.min(count - 1, pos))),
    [count, loop],
  );

  const goTo = React.useCallback(
    (index) => {
      const target = loop
        ? index + Math.round((targetRef.current - index) / count) * count
        : index;
      settle(clamp(target));
    },
    [clamp, count, loop, settle],
  );

  const nudge = React.useCallback(
    (by) => settle(clamp(Math.round(targetRef.current) + by)),
    [clamp, settle],
  );

  const onPointerDown = (event) => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    targetRef.current = posRef.current;
    dragRef.current = {
      id: event.pointerId,
      x: event.clientX,
      pos: posRef.current,
      v: 0,
      t: performance.now(),
    };
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.id !== event.pointerId) return;

    const pitch = widthRef.current * (1 + gap);
    if (!pitch) return;

    const now = performance.now();
    const previous = posRef.current;
    posRef.current = clamp(drag.pos - (event.clientX - drag.x) / pitch);
    drag.v = ((posRef.current - previous) / Math.max(now - drag.t, 1)) * 1000;
    drag.t = now;

    const index = indexAt(posRef.current);
    if (index !== selected) setSelected(index);
    paint();
  };

  const endDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.id !== event.pointerId) return;
    dragRef.current = null;
    const carried = Math.max(-2, Math.min(2, drag.v * 0.18));
    settle(clamp(Math.round(posRef.current + carried)));
  };

  useIsoLayoutEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;

    const measure = () => {
      const card = cardRefs.current[0];
      if (!card) return;
      widthRef.current = card.offsetWidth;
      paint();
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [paint]);

  React.useEffect(
    () => () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  const active = slides[selected];

  return (
    <div
      className={`cfc-root ${className}`}
      style={{ "--cf-card": cardWidth }}
      role="region"
      aria-roledescription="carousel"
      aria-label={label}
    >
      <style>{`
        .cfc-root {
          width: 100%;
          position: relative;
        }
        .cfc-frame {
          cursor: grab;
          overflow: visible;
          padding: 40px 0 60px;
          outline: none;
          touch-action: pan-y;
          user-select: none;
        }
        .cfc-frame:active { cursor: grabbing; }
        .cfc-stage {
          position: relative;
          height: calc(var(--cf-card) * 1.34);  /* 3:4 ratio so full card visible */
          transform-style: preserve-3d;
          overflow: visible;
        }
        .cfc-card {
          position: absolute;
          left: 50%;
          top: 0;
          width: var(--cf-card);
          aspect-ratio: 3/4;
          overflow: hidden;
          border-radius: 18px;
          will-change: transform;
          box-shadow: 0 20px 60px rgba(0,0,0,0.5);
        }
        .cfc-card img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          pointer-events: none;
          user-select: none;
          -webkit-user-drag: none;
        }

        /* Card label overlay */
        .cfc-card-label {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 40px 20px 20px;
          background: linear-gradient(transparent, rgba(0,0,0,0.82));
          color: #fff;
          border-radius: 0 0 18px 18px;
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .cfc-card-title {
          font-family: 'Montserrat', sans-serif;
          font-size: clamp(0.92rem, 1.3vw, 1.15rem);
          font-weight: 700;
          margin: 0 0 2px 0;
          letter-spacing: -0.01em;
        }
        .cfc-card-sub {
          font-family: 'Montserrat', sans-serif;
          font-size: clamp(0.72rem, 1vw, 0.82rem);
          font-weight: 400;
          margin: 0;
          opacity: 0.75;
          text-transform: uppercase;
          letter-spacing: 0.12em;
        }

        /* Nav buttons */
        .cfc-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 200;
          width: 42px;
          height: 42px;
          border-radius: 50%;
          border: 1px solid rgba(255,255,255,0.2);
          background: rgba(0,0,0,0.5);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s, transform 0.2s;
        }
        .cfc-nav:hover {
          background: rgba(236, 43, 40, 0.8);
          border-color: #ec2b28;
          transform: translateY(-50%) scale(1.05);
        }
        .cfc-nav-prev { left: 12px; }
        .cfc-nav-next { right: 12px; }

        /* Caption */
        .cfc-caption {
          margin-top: 20px;
          text-align: center;
          animation: cf-fade-in 0.3s ease;
        }
        @keyframes cf-fade-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .cfc-caption-title {
          font-family: 'Montserrat', sans-serif;
          font-size: clamp(1rem, 1.4vw, 1.2rem);
          font-weight: 700;
          color: #fff;
          margin: 0 0 4px 0;
          letter-spacing: -0.02em;
        }
        .cfc-caption-sub {
          font-family: 'Montserrat', sans-serif;
          font-size: clamp(0.78rem, 1vw, 0.88rem);
          color: #9ca3af;
          margin: 0;
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }

        /* Pagination dots */
        .cfc-dots {
          display: flex;
          justify-content: center;
          gap: 8px;
          margin-top: 20px;
        }
        .cfc-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #fff;
          border: none;
          cursor: pointer;
          transition: opacity 0.2s;
          padding: 0;
        }
      `}</style>

      <div className="relative">
        <div
          ref={frameRef}
          tabIndex={0}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft") { event.preventDefault(); nudge(-1); }
            else if (event.key === "ArrowRight") { event.preventDefault(); nudge(1); }
          }}
          className="cfc-frame"
          style={{ perspective: `calc(var(--cf-card) * ${perspective})` }}
        >
          <div className="cfc-stage">
            {slides.map((slide, index) => (
              <div
                key={index}
                ref={(node) => { cardRefs.current[index] = node; }}
                role="group"
                aria-roledescription="slide"
                aria-label={`${index + 1} of ${count}`}
                className={`cfc-card ${cardClassName}`}
              >
                <img src={slide.src} alt={slide.alt} draggable={false} />
                {(slide.title || slide.subtitle) && (
                  <div className="cfc-card-label" style={{ opacity: index === selected ? 1 : 0 }}>
                    {slide.title && <p className="cfc-card-title">{slide.title}</p>}
                    {slide.subtitle && <p className="cfc-card-sub">{slide.subtitle}</p>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {showNavigation && (
          <>
            <button
              type="button"
              aria-label="Previous slide"
              onClick={() => nudge(-1)}
              className="cfc-nav cfc-nav-prev"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              aria-label="Next slide"
              onClick={() => nudge(1)}
              className="cfc-nav cfc-nav-next"
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}
      </div>

      {showCaption && active?.title && (
        <div key={selected} className="cfc-caption">
          <p className="cfc-caption-title">{active.title}</p>
          {active.subtitle && <p className="cfc-caption-sub">{active.subtitle}</p>}
        </div>
      )}

      {showPagination && (
        <div className="cfc-dots">
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              aria-label={`Go to slide ${index + 1}`}
              aria-current={index === selected}
              onClick={() => goTo(index)}
              className="cfc-dot"
              style={{ opacity: index === selected ? 1 : 0.3 }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default CoverflowCarousel;
